// src/lib/lazyWithRetry.js
// React.lazy wrapper that survives deploys. After a deploy the old hashed
// chunks are gone, so an open tab that lazy-loads a page it has not visited
// yet gets a ChunkLoadError. On that error we reload the page once (fresh
// index.html -> new chunk names). A sessionStorage timestamp guards against a
// reload loop; it is cleared after any successful chunk load.
import { lazy } from "react";

const FLAG = "cbc.chunkReloadAt";
const RETRY_WINDOW_MS = 60_000;

export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === "ChunkLoadError") return true;
  const msg = String(err.message || err);
  return /Loading (CSS )?chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg);
}

function readFlag() {
  try {
    return Number(window.sessionStorage.getItem(FLAG)) || 0;
  } catch {
    return 0;
  }
}

function writeFlag(v) {
  try {
    if (v == null) window.sessionStorage.removeItem(FLAG);
    else window.sessionStorage.setItem(FLAG, String(v));
  } catch {
    /* storage unavailable */
  }
}

export function lazyWithRetry(factory) {
  return lazy(() =>
    factory().then(
      (mod) => {
        if (readFlag()) writeFlag(null);
        return mod;
      },
      (err) => {
        if (isChunkLoadError(err) && typeof window !== "undefined") {
          const last = readFlag();
          if (!last || Date.now() - last > RETRY_WINDOW_MS) {
            writeFlag(Date.now());
            window.location.reload();
            // Keep Suspense showing its fallback while the page reloads.
            return new Promise(() => {});
          }
        }
        throw err;
      }
    )
  );
}

export default lazyWithRetry;
